import React, { useEffect } from 'react';
import { useState } from 'react';
import MFab from '@/components/ui/atoms/MFab';
import { useTranslation } from 'react-i18next';
import RemindersListing from './RemindersListing';
import { getMemberInfo } from '@/hooks/useMembers';
import MDropDownDrawer from '@/components/ui/molecules/MDropDownDrawer';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DerivedMemberType } from '@/types/member';
import { ReminderType } from '@/types/reminders';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { trackUserDataLoaded } from '@/utils/gtm';
import { ANALYTICS_SCREEN_TYPES, ANALYTICS_SCREEN_NAMES } from '@/constants/analytics.constants';

/**
 * Reminders page component
 * Shows a list of reminders if they exist, otherwise shows a create reminder button
 */
const Reminders: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const loggedInMember = useSelector((state: RootState) => state.auth?.member);
  const memberInfo = getMemberInfo();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Reset to logged-in user on reminders page mount
  // COMMENTED OUT: Preserve selected member when navigating to reminders landing
  // React.useEffect(() => {
  //   if (loggedInMember) {
  //     dispatch(clearSelectedMember());
  //   }
  // }, []);

  // Track page load once
  useEffect(() => {
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
      screenName: ANALYTICS_SCREEN_NAMES.REMINDER_HOME,
    });
  }, []);

  const getReminderItems = (): { value: string; label: string }[] => {
    const items = [
      ReminderType.APPOINTMENT,
      ReminderType.MEDICATION,
      ReminderType.VACCINATION,
      ReminderType.INSURANCE,
    ];
    if (memberInfo.derivedMemberType == DerivedMemberType.P) {
      items.push(ReminderType.PET_CARE);
    }
    return items.map((item) => ({ value: item, label: t(`reminders.filterTypes.${item}`) }));
  };

  const handleCreateReminderType = (type: string) => {
    setDrawerOpen(false);
    console.log(`Create reminder of type: ${type}`);
    switch (type) {
      case ReminderType.APPOINTMENT:
        navigate('/reminders/appointment');
        break;
      case ReminderType.INSURANCE:
        navigate('/reminders/insurance');
        break;
      case ReminderType.MEDICATION:
        navigate('/reminders/medication');
        break;
      case ReminderType.VACCINATION:
        navigate('/reminders/vaccination-tracker');
        break;
      case ReminderType.PET_CARE:
        navigate('/reminders/pet-care');
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (searchParams.get('mode') === 'add') {
      setDrawerOpen(true);
      setSearchParams({});
      
      // Track add reminder when drawer opens
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
        screenName: ANALYTICS_SCREEN_NAMES.REMINDER_ADD,
      });
    }
  }, [searchParams]);

  return (
    <div className="profile-page-root">
      <MFab />
      <RemindersListing
        onAddReminder={() => {
          // Track add reminder when button is clicked
          trackUserDataLoaded({
            memberInfo,
            screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
            screenName: ANALYTICS_SCREEN_NAMES.REMINDER_ADD,
          });
          setDrawerOpen(true);
        }}
      />

      <MDropDownDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={t('reminders.selectReminderType')}
        items={getReminderItems()}
        onSelect={handleCreateReminderType}
      />
    </div>

  );
};

export default Reminders;
